import { ContractStage } from '../../../models/Entities/Contracts/ContractStage';
import { ProtocolConsumption } from '../../../models/Entities/Protocols/ProtocolConsumption';
import { ProtocolPatient } from '../../../models/Entities/Protocols/ProtocolPatient';
import { PatientVisit } from '../../../models/Entities/Protocols/PatientVisist';
import { ProtocolVisit } from '../../../models/Entities/Protocols/ProtocolVisit';
import { PatientStage, statusStage } from './PatientStatusStatistics.Functions';

/**
 * Ejecución de visitas del protocolo, desde dos fuentes posibles.
 *
 * - Con contrato: el plan son las visitas del contrato, y una visita está
 *   realizada cuando el paciente tiene al menos un consumo cargado en ella.
 * - Sin contrato: el plan son las visitas del protocolo (flowchart del registro
 *   clínico), y el estado sale de las visitas registradas de cada paciente.
 *
 * Las dos arman el mismo modelo, así la pantalla es una sola.
 */

//#region Modelo común

/** Situación de un paciente en una visita del plan. */
export type VisitCellState = 'done' | 'started' | 'skipped' | 'failed';

export interface VisitColumn {
  /** Identificador de la visita dentro de su fuente (id de etapa o crossVersionId). */
  key: string;
  name: string;
  order: number;
  isOptional: boolean;
}

export interface VisitCell {
  state: VisitCellState;
  /** Fecha de la visita; undefined si no la tiene. */
  time?: number;
}

export interface VisitMatrixRow {
  numberInProtocol: string;
  status: number;
  statusDescription?: string;
  stage: PatientStage;
  /** Celdas por clave de columna. Una visita sin registro no tiene celda. */
  cells: Map<string, VisitCell>;
  /** Visitas obligatorias realizadas por el paciente. */
  doneRequired: number;
}

export interface VisitProgress {
  /** Visitas del plan que no son opcionales. */
  requiredVisits: number;
  randomizedPatients: number;
  /** Visitas obligatorias × pacientes randomizados. */
  expected: number;
  /** Visitas obligatorias realizadas por pacientes randomizados. */
  doneExpected: number;
  /** Todas las visitas realizadas del plan, de cualquier paciente de la lista. */
  doneTotal: number;
  /** doneExpected sobre expected, en entero. */
  percentage: number;
}

export interface VisitTimelinePoint {
  /** Mes en formato AAAA-MM. */
  month: string;
  count: number;
  /** Acumulado hasta ese mes inclusive. */
  cumulative: number;
}

export interface VisitTimeline {
  points: VisitTimelinePoint[];
  /** Visitas realizadas sin fecha: no se pueden ubicar en el tiempo. */
  undated: number;
}

export interface VisitExecutionModel {
  columns: VisitColumn[];
  rows: VisitMatrixRow[];
  progress: VisitProgress;
  timeline: VisitTimeline;
  /** Visitas registradas fuera del plan (sólo en la fuente del registro clínico). */
  extraVisits: number;
}

//#endregion Modelo común

//#region Helpers

const toTime = (value: Date | string | undefined | null): number | undefined => {
  if (!value) return undefined;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? undefined : time;
};

/** El mismo criterio que usa la actividad del protocolo para comparar pacientes. */
const normalizePatient = (value?: string | null): string => (value ?? '').trim().toLowerCase();

const byPatientNumber = (a: ProtocolPatient, b: ProtocolPatient): number =>
  (a.numberInProtocol ?? '').localeCompare(b.numberInProtocol ?? '', 'es', { numeric: true, sensitivity: 'base' });

const monthKey = (time: number): string => {
  const date = new Date(time);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
};

/**
 * Visitas realizadas por mes, con el acumulado. Los meses sin visitas entre el
 * primero y el último se completan con 0: sin ellos, un parate de tres meses no
 * se vería en el gráfico, que es justamente lo que tiene que mostrar.
 */
export const buildMonthlyTimeline = (times: (number | undefined)[]): VisitTimeline => {
  const counts = new Map<string, number>();
  let undated = 0;

  times.forEach((time) => {
    if (time === undefined) {
      undated += 1;
      return;
    }
    const key = monthKey(time);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  });

  const months = Array.from(counts.keys()).sort();
  if (months.length === 0) return { points: [], undated };

  const [firstYear, firstMonth] = months[0].split('-').map(Number);
  const [lastYear, lastMonth] = months[months.length - 1].split('-').map(Number);

  const points: VisitTimelinePoint[] = [];
  let cumulative = 0;
  for (let year = firstYear, month = firstMonth; year < lastYear || (year === lastYear && month <= lastMonth); ) {
    const key = `${year}-${String(month).padStart(2, '0')}`;
    const count = counts.get(key) ?? 0;
    cumulative += count;
    points.push({ month: key, count, cumulative });
    month += 1;
    if (month > 12) {
      month = 1;
      year += 1;
    }
  }

  return { points, undated };
};

/**
 * Arma el modelo a partir del plan, los pacientes y una función que dice qué pasó
 * con cada paciente en cada visita.
 *
 * Se espera que cada paciente RANDOMIZADO haga cada visita OBLIGATORIA. Los que
 * están en selección todavía no pueden tener visitas de tratamiento, las fallas
 * de selección nunca las van a tener, y una visita opcional no es una deuda: si
 * entraran en la cuenta, el porcentaje quedaría más bajo que el avance real.
 */
const buildExecution = (
  columns: VisitColumn[],
  patients: ProtocolPatient[],
  cellFor: (column: VisitColumn, numberInProtocol: string) => VisitCell | undefined,
  timelineTimes: (number | undefined)[],
  extraVisits: number
): VisitExecutionModel => {
  const rows: VisitMatrixRow[] = patients
    .slice()
    .sort(byPatientNumber)
    .map((patient) => {
      const cells = new Map<string, VisitCell>();
      let doneRequired = 0;
      columns.forEach((column) => {
        const cell = cellFor(column, patient.numberInProtocol);
        if (!cell) return;
        cells.set(column.key, cell);
        if (cell.state === 'done' && !column.isOptional) doneRequired += 1;
      });
      return {
        numberInProtocol: patient.numberInProtocol,
        status: patient.status,
        statusDescription: patient.statusDescription,
        stage: statusStage(patient.status),
        cells,
        doneRequired
      };
    });

  const requiredVisits = columns.filter((column) => !column.isOptional).length;
  const randomizedRows = rows.filter((row) => row.stage === 'randomized');
  const doneExpected = randomizedRows.reduce((acc, row) => acc + row.doneRequired, 0);
  let doneTotal = 0;
  rows.forEach((row) =>
    row.cells.forEach((cell) => {
      if (cell.state === 'done') doneTotal += 1;
    })
  );
  const expected = requiredVisits * randomizedRows.length;

  return {
    columns,
    rows,
    progress: {
      requiredVisits,
      randomizedPatients: randomizedRows.length,
      expected,
      doneExpected,
      doneTotal,
      percentage: expected > 0 ? Math.round((doneExpected / expected) * 100) : 0
    },
    timeline: buildMonthlyTimeline(timelineTimes),
    extraVisits
  };
};

//#endregion Helpers

//#region Fuente: contrato + consumos

/** Visitas del contrato en el orden del plan (stageOrder, y el id para desempatar). */
export const buildContractVisitColumns = (stages: ContractStage[]): VisitColumn[] =>
  stages
    .slice()
    .sort((a, b) => (a.stageOrder ?? 0) - (b.stageOrder ?? 0) || a.id - b.id)
    .map((stage) => ({
      key: String(stage.id),
      name: stage.stageName || `Visita ${stage.id}`,
      order: stage.stageOrder ?? 0,
      isOptional: !!stage.isOptional
    }));

/**
 * Pares paciente-visita con consumos, con la fecha de realización del primer
 * consumo de cada uno. Un par sin ningún consumo fechado queda con fecha undefined:
 * está realizado, sólo que no se puede ubicar en el tiempo.
 */
export const buildDonePairs = (consumptions: ProtocolConsumption[]): Map<string, number | undefined> => {
  const pairs = new Map<string, number | undefined>();
  consumptions.forEach((consumption) => {
    if (!consumption.stageId || !consumption.patientNumberInProtocol) return;
    const key = `${consumption.stageId}::${normalizePatient(consumption.patientNumberInProtocol)}`;
    const time = toTime(consumption.serviceDueDate);
    const current = pairs.get(key);
    if (!pairs.has(key)) {
      pairs.set(key, time);
    } else if (time !== undefined && (current === undefined || time < current)) {
      pairs.set(key, time);
    }
  });
  return pairs;
};

export const buildContractVisitExecution = (
  stages: ContractStage[],
  patients: ProtocolPatient[],
  consumptions: ProtocolConsumption[]
): VisitExecutionModel => {
  const pairs = buildDonePairs(consumptions);
  return buildExecution(
    buildContractVisitColumns(stages),
    patients,
    (column, numberInProtocol) => {
      const key = `${column.key}::${normalizePatient(numberInProtocol)}`;
      return pairs.has(key) ? { state: 'done', time: pairs.get(key) } : undefined;
    },
    Array.from(pairs.values()),
    0
  );
};

//#endregion Fuente: contrato + consumos

//#region Fuente: registro clínico (sin contrato)

/**
 * Estado de una visita registrada del paciente. Mismas etiquetas que la lista de
 * visitas del profesional (PractitionerProtocolVisit.List.Functions).
 */
export const PATIENT_VISIT_STATUS_LABEL: Record<number, string> = {
  0: 'Iniciada',
  1: 'Completada',
  2: 'Salteada',
  3: 'Falla de selección',
  4: 'Modificada',
  5: 'Auto completada'
};

/**
 * Cuenta como realizada si está Completada, Modificada o Auto completada. Una
 * visita Iniciada todavía no terminó; una Salteada o con Falla de selección no se
 * hizo. Un estado desconocido se toma como iniciada: hay un registro, pero no
 * alcanza para afirmar que la visita se completó.
 */
export const patientVisitCellState = (status: number): VisitCellState => {
  switch (status) {
    case 1:
    case 4:
    case 5:
      return 'done';
    case 2:
      return 'skipped';
    case 3:
      return 'failed';
    default:
      return 'started';
  }
};

/*
 * Notas y visitas extra-protocolo: la misma regla que la actividad del protocolo
 * (Protocol.Activity.Functions: IsNoteVisit / IsExtraProtocolPatientVisit). Se
 * repite acá para que las estadísticas no dependan de ese módulo, que arrastra la
 * conexión con el store.
 */
const isNoteVisit = (visit: PatientVisit): boolean =>
  (visit.title || '').trim().toLowerCase() === 'note' || (visit.type || '').trim().toLowerCase() === 'note';

const isExtraProtocolVisit = (visit: PatientVisit): boolean =>
  !isNoteVisit(visit) &&
  ((visit.type || '').trim().toLowerCase() === 'extraprotocolvisit' ||
    (visit.title || '').trim().toLowerCase() === 'extraprotocolvisit' ||
    !visit.crossVersionId);

/** Si una visita tiene varios registros, vale el más avanzado. */
const STATE_RANK: Record<VisitCellState, number> = { done: 3, started: 2, skipped: 1, failed: 0 };

/** Visitas del plan del protocolo, una por crossVersionId, en orden. */
export const buildEmrVisitColumns = (planVisits: ProtocolVisit[]): VisitColumn[] => {
  const byCrossVersion = new Map<number, ProtocolVisit>();
  (planVisits || []).forEach((visit) => {
    if (!visit.crossVersionId) return;
    const current = byCrossVersion.get(visit.crossVersionId);
    if (!current || (visit.order ?? 0) < (current.order ?? 0)) byCrossVersion.set(visit.crossVersionId, visit);
  });

  return Array.from(byCrossVersion.values())
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.crossVersionId - b.crossVersionId)
    .map((visit) => ({
      key: String(visit.crossVersionId),
      name: visit.title || `Visita ${visit.order}`,
      order: visit.order ?? 0,
      isOptional: false
    }));
};

export const buildEmrVisitExecution = (
  planVisits: ProtocolVisit[],
  patients: ProtocolPatient[],
  patientVisits: PatientVisit[]
): VisitExecutionModel => {
  const visits = (patientVisits || []).filter((visit) => !isNoteVisit(visit));
  const extraVisits = visits.filter(isExtraProtocolVisit).length;

  // Una visita del paciente cae en la del plan por crossVersionId, como en la actividad.
  const cells = new Map<string, VisitCell>();
  visits
    .filter((visit) => !isExtraProtocolVisit(visit))
    .forEach((visit) => {
      const key = `${visit.crossVersionId}::${normalizePatient(visit.numberInProtocol)}`;
      const candidate: VisitCell = { state: patientVisitCellState(visit.status), time: toTime(visit.date) };
      const current = cells.get(key);
      const isBetter =
        !current ||
        STATE_RANK[candidate.state] > STATE_RANK[current.state] ||
        (candidate.state === current.state &&
          candidate.time !== undefined &&
          (current.time === undefined || candidate.time < current.time));
      if (isBetter) cells.set(key, candidate);
    });

  const doneTimes = Array.from(cells.values())
    .filter((cell) => cell.state === 'done')
    .map((cell) => cell.time);

  return buildExecution(
    buildEmrVisitColumns(planVisits),
    patients,
    (column, numberInProtocol) => cells.get(`${column.key}::${normalizePatient(numberInProtocol)}`),
    doneTimes,
    extraVisits
  );
};

//#endregion Fuente: registro clínico (sin contrato)

/** Etiqueta corta de mes para el eje: "mar 2026". */
export const formatMonth = (month: string): string => {
  const [year, monthNumber] = month.split('-').map(Number);
  const date = new Date(year, monthNumber - 1, 1);
  return date.toLocaleDateString('es-AR', { month: 'short', year: 'numeric' });
};

/** Fecha corta para las celdas de la matriz: "05/03/26". */
export const formatShortDate = (time?: number): string => {
  if (time === undefined) return '';
  return new Date(time).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit' });
};

/**
 * Fechas de las visitas completadas de un protocolo, para la línea de tiempo de la
 * cartera, donde no se carga el plan de cada protocolo.
 *
 * Cuenta una por visita realizada: si una misma visita tiene varios registros, vale
 * la fecha más temprana. Las notas no son visitas y se descartan; las visitas fuera
 * del plan sí se cuentan, porque también son visitas hechas.
 */
export const buildCompletedVisitTimes = (patientVisits: PatientVisit[]): (number | undefined)[] => {
  const byVisit = new Map<string, number | undefined>();

  (patientVisits || [])
    .filter((visit) => !isNoteVisit(visit) && patientVisitCellState(visit.status) === 'done')
    .forEach((visit) => {
      const key = isExtraProtocolVisit(visit)
        ? `extra::${visit.id}`
        : `${visit.crossVersionId}::${normalizePatient(visit.numberInProtocol)}`;
      const time = toTime(visit.date);
      const current = byVisit.get(key);
      if (!byVisit.has(key)) {
        byVisit.set(key, time);
      } else if (time !== undefined && (current === undefined || time < current)) {
        byVisit.set(key, time);
      }
    });

  return Array.from(byVisit.values());
};
